import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import {
  BlockedMenuItem,
  IconMenuButton,
  IconMenuButtonContent,
  IconMenuButtonTrigger,
  IconMenuLoading,
  MoreVertGlyph,
  PressableMenuItem,
} from "@/components/ui/icon-menu-button";
import { WAITING_AFFORDANCE_TITLE } from "@/components/ui/disabled-tooltip";
import { addVariantBlock, deleteBlock, type CatalogDetailItem } from "@/lib/catalog-item-detail";

/** `Report` and `Copy Link`: `sx={{ py: 1.3 }}`, 10.4px, on the page and in the dialog. */
const TALL_ROW = "py-[10.4px]";

// The details page's action menu — `Header/ActionMenu.tsx` — and the delete
// confirmation it opens.
//
// The menu is four items and two refusals, and every one of them is a gate the
// donor already decided:
//
//   - **Add Variant** is disabled for the Unspecified manufacturer, with a
//     tooltip saying so.
//   - **Edit** and **Delete** render only for a user who passes the edit ladder
//     — a *render* gate, not a disable gate, so a viewer sees a two-item menu.
//   - **Delete** is additionally disabled while anything uses the item, and
//     while the usage count is unknown (`?? 1` — the donor errs toward
//     refusing). Python refuses such a delete anyway; this is the affordance.
//   - **Report** sits between Edit and Copy Link in the donor and is absent
//     here: spec #238 defers Item Report to the Specifications map (scope-cuts
//     resolution #204).
//
// The trigger, the menu box and the blocked rows are the shared
// `icon-menu-button` pieces (#456), so the catalog list's item menu — ticket 2
// of 3, not yet built — composes them rather than copying them. Not the app's `ActionsMenu`
// composite: that one is the port of a *different* donor menu — a labelled
// "Actions" button whose every entry carries an icon and a description — and
// bending it to a bare icon trigger with plain text rows would distort a shared
// component to serve one caller.
//
// One behaviour the shared blocked row adds over what this page used to do: its
// reason is now reachable by the menu's arrow walk as well as by hover. The
// donor's is pointer-only, and so was this. See `icon-menu-button.tsx`.

/**
 * What the menu reads off its item. The whole item once its read lands; before
 * that, whatever the dialog's opener held (#964 N16) — the donor's ⋮ asks only
 * that its object cache hold *something* for the id (`ActionMenu.tsx:72`), so
 * a row's `{id, name, heroImgUrl}` partial draws it too. A partial with no
 * manufacturer refuses no Add Variant, as the donor's `manufacturerId`
 * undefined refuses none.
 */
export type ItemDetailActionsItem = Pick<CatalogDetailItem, "id"> &
  Partial<Pick<CatalogDetailItem, "manufacturer" | "category" | "usageCount">>;

interface ItemDetailActionsProps {
  /** `null` while nothing stands for the item — the donor draws no menu at all. */
  item: ItemDetailActionsItem | null;
  /** Whether this user passes the item edit ladder. */
  canEdit: boolean;
  /**
   * True while the usage count the Delete gate reads is still in flight. The
   * menu shows a spinner in place of its items rather than answering a gate
   * from data that has not arrived.
   */
  isLoadingUsage?: boolean;
  /**
   * Whether the displayed item's usage read has landed. The donor's Delete
   * gate reads its count from that read alone — `specificationCount ?? 1`
   * (`ActionMenu.tsx:122`) — so when it failed, Delete stays blocked at one
   * rather than trusting the summary's column.
   */
  breakdownArrived?: boolean;
  /** The confirmed delete — absent when the host confirms it (`onDeleteRequested`). */
  onDelete?: () => void;
  isDeleting?: boolean;
  onCopyLink: () => void;
  onAddVariant?: () => void;
  /**
   * The host confirms Delete itself: the press is handed over and this menu
   * opens no confirmation of its own — the Item Search island's dialog (#798),
   * which closes on the press.
   */
  onDeleteRequested?: () => void;
  /**
   * The island's dialog arrangement (#798, `renderingMode === 'dialog'`): Edit
   * opens in a new tab, and Report sits between Edit and Copy Link.
   */
  inDialog?: boolean;
  /**
   * The menu opened — the donor's usage read fires on this press
   * (`Header/ActionMenu.tsx:40-56`), and every card kebab for the item shares it.
   */
  onMenuOpen?: () => void;
}

export function ItemDetailActions({
  item,
  canEdit,
  isLoadingUsage = false,
  breakdownArrived = true,
  onDelete,
  isDeleting = false,
  onCopyLink,
  onAddVariant,
  onDeleteRequested,
  inDialog = false,
  onMenuOpen,
}: ItemDetailActionsProps) {
  const [confirming, setConfirming] = useState(false);

  // `ActionMenu.tsx:47-49` — no item, no menu. Not a disabled trigger: there is
  // nothing for any of these verbs to act on.
  if (!item) return null;

  const addVariantReason = item.manufacturer
    ? addVariantBlock({ manufacturer: item.manufacturer, category: item.category })
    : null;
  const deleteReason = deleteBlock(breakdownArrived ? item.usageCount : undefined);

  let contents: ReactNode;
  if (isLoadingUsage) {
    // `ActionMenu.tsx:157-163` — the trigger stays, the rows are replaced.
    contents = <IconMenuLoading />;
  } else {
    contents = (
      <>
        {addVariantReason ? (
          <BlockedMenuItem
            label="Add Variant"
            reason={addVariantReason}
            side="top"
            testIdPrefix="item-action"
          />
        ) : (
          <PressableMenuItem
            data-testid="item-action-Add Variant"
            onSelect={() => onAddVariant?.()}
          >
            Add Variant
          </PressableMenuItem>
        )}

        {canEdit && (
          // A typed `Link` now the edit route is on the tree (#253) — it was a
          // plain anchor only because `to` could not compile against a route
          // that did not exist. The typed form is what makes a rename of the
          // route a compile error here instead of a dead menu entry. No
          // `target` on the page, because `renderingMode === 'page'` leaves it
          // undefined in the donor (`ActionMenu.tsx:196`); `_blank` in the
          // island's dialog, which is its other arm.
          <PressableMenuItem asChild>
            <Link
              data-testid="item-action-Edit"
              to="/items/$itemId/edit"
              params={{ itemId: String(item.id) }}
              {...(inDialog ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              Edit
            </Link>
          </PressableMenuItem>
        )}

        {/* The island's dialog draws Report here, between Edit and Copy Link.
            Item Report is Distribution's (#551), so the row is the waiting
            affordance the read-only menu already carries. The page leaves it
            out, by #238's deferral.

            Both rows are `sx={{ py: 1.3 }}` — 10.4px, against every other
            row's 6px (`ActionMenu.tsx:232,236`) — on the page as in the
            dialog, as the donor's one `ActionMenu` serves both (#850 D9 a). */}
        {inDialog && (
          <BlockedMenuItem
            label="Report"
            reason={WAITING_AFFORDANCE_TITLE}
            side="bottom"
            testIdPrefix="item-action"
            className={TALL_ROW}
          />
        )}

        <PressableMenuItem
          data-testid="item-action-Copy Link"
          className={TALL_ROW}
          onSelect={() => onCopyLink()}
        >
          Copy Link
        </PressableMenuItem>

        {canEdit &&
          (deleteReason ? (
            // `showCallout`, which the Add Variant row above deliberately does
            // not pass (`ActionMenu.tsx:211` against `:177`).
            <BlockedMenuItem
              label="Delete"
              reason={deleteReason}
              side="bottom"
              arrow
              testIdPrefix="item-action"
            />
          ) : (
            <PressableMenuItem
              data-testid="item-action-Delete"
              onSelect={() => (onDeleteRequested ? onDeleteRequested() : setConfirming(true))}
            >
              Delete
            </PressableMenuItem>
          ))}
      </>
    );
  }

  return (
    <>
      <ItemActionMenu onOpen={onMenuOpen}>{contents}</ItemActionMenu>

      {onDelete && (
        <ConfirmDeleteDialog
          open={confirming}
          onOpenChange={setConfirming}
          label="item"
          onConfirm={onDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}

/**
 * The menu in the island's `readOnly` mode — the spec details page's item
 * dialog (#695): with `readOnly && authenticated` the donor's `ActionMenu`
 * offers **only "Report"** (`ActionMenu.tsx:186-192`), which opens Item Report.
 * That verb is Distribution's (#551), so the row is a waiting affordance: there,
 * blocked, with the shared sentence. The same trigger and paper as the page's.
 */
export function ItemReadOnlyActions({
  visible,
}: {
  /**
   * The donor's `!item` gate (`Header/ActionMenu.tsx:72`) — passed by the
   * dialog's seeded partial as well as by the full item, so the menu is up
   * while the item still loads.
   */
  visible: boolean;
}) {
  if (!visible) return null;
  return (
    <ItemActionMenu>
      <BlockedMenuItem
        label="Report"
        reason={WAITING_AFFORDANCE_TITLE}
        side="bottom"
        testIdPrefix="item-action"
      />
    </ItemActionMenu>
  );
}

/** The details page's action menu shell — its trigger and paper — around its rows. */
function ItemActionMenu({ children, onOpen }: { children: ReactNode; onOpen?: () => void }) {
  return (
    <IconMenuButton onOpenChange={(open) => open && onOpen?.()}>
      {/* The donor's `mr: 2` — 16px of clearance from the neighbouring
          header control. On a wrapper, not on the trigger: the spacing is
          this page's layout, and the trigger's own class list is the shared
          component's business. */}
      <span className="mr-4 inline-flex">
        <IconMenuButtonTrigger label="More options" box="outlined">
          <MoreVertGlyph />
        </IconMenuButtonTrigger>
      </span>
      {/* `slotProps.paper.style.minWidth: '150px'` — this page's donor
          sets it explicitly (`ActionMenu.tsx:81-84`) and the catalog list's
          does not (`menu-button.tsx:97-112` passes the `Menu` no paper
          props at all, so that one auto-sizes to its widest row). It rode
          the shared box until #457's parity review found it widening the
          list menu by ~13px of trailing whitespace. */}
      {/* `anchorOrigin.horizontal: 'center'` against a `'right'` transform
          origin (`ActionMenu.tsx:78-79`) — this donor hangs its paper's right
          edge over the *middle* of the trigger, where the catalog list's
          hangs it off the trigger's right edge (`menu-button.tsx:102-108`).
          Half of the shared trigger's 34px, which its own box tests pin.
          Its rows are bare `MenuItem` text, `body1`: 36px rows at ≥600px,
          measured on local Modsy's item page (#911). */}
      <IconMenuButtonContent minWidthPx={150} alignOffsetPx={17} rowVariant="body1">
        {children}
      </IconMenuButtonContent>
    </IconMenuButton>
  );
}
